import React, { useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import Select from '../../../../components/Select';
import ModuleSection from '../ModuleSection';
import blogStorage from '../../utils/storage';
import './index.scss';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('blog');
  const [isSaving, setIsSaving] = useState(false);

  const tabs = [
    { id: 'blog', label: '博客信息' },
    { id: 'display', label: '显示设置' },
    { id: 'editor', label: '编辑器设置' },
    { id: 'privacy', label: '隐私设置' },
    { id: 'data', label: '数据管理' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  // 应用显示设置
  useEffect(() => {
    if (formData.displaySettings) {
      // 应用字体大小
      if (formData.displaySettings.fontSize) {
        document.documentElement.style.setProperty('--font-size-base', 
          `${formData.displaySettings.fontSize}px`);
      }
      
      // 应用主题
      if (formData.displaySettings.theme) {
        document.documentElement.setAttribute('data-theme', formData.displaySettings.theme);
      }
      
      // 应用布局模式
      if (formData.displaySettings.layout) {
        document.documentElement.setAttribute('data-layout', formData.displaySettings.layout);
      }
    }
  }, [formData.displaySettings]);

  const loadSettings = () => {
    const settingsData = blogStorage.getSettings();
    setSettings(settingsData);
    setFormData(settingsData);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      blogStorage.updateSettings(formData);
      alert('设置保存成功');
      loadSettings();
    } catch (error) {
      console.error('保存设置失败:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportData = () => {
    try {
      const data = blogStorage.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blog-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('数据导出成功');
    } catch (error) {
      console.error('导出数据失败:', error);
      alert('导出失败，请重试');
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (confirm('导入数据将覆盖现有数据，确定要继续吗？')) {
          const success = blogStorage.importData(data);
          if (success) {
            alert('数据导入成功');
            loadSettings();
          } else {
            alert('数据格式错误，导入失败');
          }
        }
      } catch (error) {
        console.error('导入数据失败:', error);
        alert('文件格式错误，请选择正确的备份文件');
      }
    };
    reader.readAsText(file);
  };

  const handleClearData = () => {
    if (confirm('清空数据将删除所有文章、分类、标签等信息，此操作不可恢复，确定要继续吗？')) {
      if (confirm('再次确认：真的要清空所有数据吗？')) {
        blogStorage.clearAllData();
        alert('数据已清空');
        loadSettings();
      }
    }
  };

  if (!settings) {
    return (
      <div className="settings">
        <div className="loading">加载设置中...</div>
      </div>
    );
  }

  const renderBlogSettings = () => (
    <ModuleSection
      title="博客信息"
      variant="settings"
    >
      <div className="form-group">
        <label>博客标题</label>
        <input
          type="text"
          value={formData.blogInfo?.title || ''}
          onChange={(e) => handleInputChange('blogInfo', 'title', e.target.value)}
          placeholder="请输入博客标题"
          className="form-input"
        />
      </div>
      
      <div className="form-group">
        <label>博客描述</label>
        <textarea
          value={formData.blogInfo?.description || ''}
          onChange={(e) => handleInputChange('blogInfo', 'description', e.target.value)}
          placeholder="请输入博客描述"
          className="form-textarea"
          rows={3}
        />
      </div>
      
      <div className="form-group">
        <label>作者名称</label>
        <input
          type="text"
          value={formData.blogInfo?.author || ''}
          onChange={(e) => handleInputChange('blogInfo', 'author', e.target.value)}
          placeholder="请输入作者名称"
          className="form-input"
        />
      </div>
    </ModuleSection>
  );

  const renderDisplaySettings = () => (
    <ModuleSection
      title="显示设置"
      variant="settings"
    >
      <div className="form-group">
        <label>主题</label>
        <Select
          value={formData.displaySettings?.theme || 'dark'}
          onChange={(value) => handleInputChange('displaySettings', 'theme', value)}
          options={[
            { value: 'dark', label: '深色主题' },
            { value: 'light', label: '浅色主题' }
          ]}
          placeholder="请选择主题"
          dropdownPortal={true}
        />
      </div>
      
      <div className="form-group">
        <label>字体大小</label>
        <Select
          value={formData.displaySettings?.fontSize || 14}
          onChange={(value) => handleInputChange('displaySettings', 'fontSize', parseInt(value))}
          options={[
            { value: 12, label: '小 (12px)' },
            { value: 14, label: '中 (14px)' },
            { value: 16, label: '大 (16px)' },
            { value: 18, label: '超大 (18px)' }
          ]}
          placeholder="请选择字体大小"
          dropdownPortal={true}
        />
      </div>
      
      <div className="form-group">
        <label>布局模式</label>
        <Select
          value={formData.displaySettings?.layout || 'compact'}
          onChange={(value) => handleInputChange('displaySettings', 'layout', value)}
          options={[
            { value: 'compact', label: '紧凑' },
            { value: 'comfortable', label: '舒适' },
            { value: 'spacious', label: '宽松' }
          ]}
          placeholder="请选择布局模式"
          dropdownPortal={true}
        />
      </div>
    </ModuleSection>
  );

  const renderEditorSettings = () => (
    <ModuleSection
      title="编辑器设置"
      variant="settings"
    >
      <div className="form-group">
        <label>默认分类</label>
        <Select
          value={formData.editorSettings?.defaultCategory || ''}
          onChange={(value) => handleInputChange('editorSettings', 'defaultCategory', value)}
          options={[
            { value: '', label: '请选择默认分类' },
            ...blogStorage.getCategories().map(category => ({
              value: category.id,
              label: category.name
            }))
          ]}
          placeholder="请选择默认分类"
          dropdownPortal={true}
        />
      </div>
      
      <div className="form-group">
        <label>编辑器模式</label>
        <Select
          value={formData.editorSettings?.editorMode || 'rich'}
          onChange={(value) => handleInputChange('editorSettings', 'editorMode', value)}
          options={[
            { value: 'rich', label: '富文本' },
            { value: 'markdown', label: 'Markdown' },
            { value: 'plain', label: '纯文本' }
          ]}
          placeholder="请选择编辑器模式"
          dropdownPortal={true}
        />
      </div>
      
      <div className="form-group">
        <label>自动保存间隔</label>
        <Select
          value={formData.editorSettings?.autoSaveInterval || 30000}
          onChange={(value) => handleInputChange('editorSettings', 'autoSaveInterval', parseInt(value))}
          options={[
            { value: 10000, label: '10秒' },
            { value: 30000, label: '30秒' },
            { value: 60000, label: '1分钟' },
            { value: 300000, label: '5分钟' }
          ]}
          placeholder="请选择自动保存间隔"
          dropdownPortal={true}
        />
      </div>
    </ModuleSection>
  );

  const renderPrivacySettings = () => (
    <ModuleSection
      title="隐私设置"
      variant="settings"
    >
      <div className="form-group">
        <label>文章可见性</label>
        <Select
          value={formData.privacySettings?.articleVisibility || 'public'}
          onChange={(value) => handleInputChange('privacySettings', 'articleVisibility', value)}
          options={[
            { value: 'public', label: '公开' },
            { value: 'private', label: '私有' },
            { value: 'password', label: '密码保护' }
          ]}
          placeholder="请选择文章可见性"
          dropdownPortal={true}
        />
      </div>
      
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.privacySettings?.allowComments || false}
            onChange={(e) => handleInputChange('privacySettings', 'allowComments', e.target.checked)}
          />
          允许评论
        </label>
      </div>
      
      <div className="form-group">
        <label>访问控制</label>
        <Select
          value={formData.privacySettings?.accessControl || 'open'}
          onChange={(value) => handleInputChange('privacySettings', 'accessControl', value)}
          options={[
            { value: 'open', label: '完全开放' },
            { value: 'register', label: '需要注册' },
            { value: 'invite', label: '邀请制' }
          ]}
          placeholder="请选择访问控制"
          dropdownPortal={true}
        />
      </div>
    </ModuleSection>
  );

  const renderDataManagement = () => (
    <ModuleSection
      title="数据管理"
      variant="settings"
    >
      <div className="data-actions">
        <div className="action-group">
          <h5>数据备份</h5>
          <p>导出所有博客数据，包括文章、分类、标签等</p>
          <Button 
            type="primary"
            onClick={handleExportData}
          >
            导出数据
          </Button>
        </div>
        
        <div className="action-group">
          <h5>数据导入</h5>
          <p>从备份文件恢复数据，将覆盖现有数据</p>
          <input
            type="file"
            accept=".json"
            onChange={handleImportData}
            className="file-input"
            id="import-file"
          />
          <Button as="label" htmlFor="import-file">
            选择文件导入
          </Button>
        </div>
        
        <div className="action-group danger">
          <h5>清空数据</h5>
          <p>删除所有数据，此操作不可恢复</p>
          <Button 
            type="danger"
            onClick={handleClearData}
          >
            清空所有数据
          </Button>
        </div>
      </div>
    </ModuleSection>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'blog':
        return renderBlogSettings();
      case 'display':
        return renderDisplaySettings();
      case 'editor':
        return renderEditorSettings();
      case 'privacy':
        return renderPrivacySettings();
      case 'data':
        return renderDataManagement();
      default:
        return renderBlogSettings();
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <Button 
          type="primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '保存中...' : '保存设置'}
        </Button>
      </div>

      <div className="settings-content">
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
        
        <div className="settings-main">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
